import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, IdCard, UserPlus } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import ForgotPasswordLink from "@/components/ForgotPasswordLink";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COUNTRIES = [
  { code: "AE", name: "United Arab Emirates", currency: "AED" },
  { code: "IN", name: "India", currency: "INR" },
  { code: "SA", name: "Saudi Arabia", currency: "SAR" },
  { code: "GB", name: "United Kingdom", currency: "GBP" },
  { code: "DE", name: "Germany", currency: "EUR" },
  { code: "US", name: "United States", currency: "USD" },
];

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function TabButton({ active, children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex-1 h-11 rounded-xl text-sm font-semibold transition-all"
      style={{
        background: active ? "var(--navy)" : "transparent",
        color: active ? "#fff" : "var(--text-muted)",
      }}
    >
      {children}
    </button>
  );
}

export default function CustomerLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const googleToken = sp.get("google") || "";
  const initialTab =
    googleToken ||
    sp.get("mode") === "join" ||
    sp.get("ref") ||
    window.location.pathname === "/join"
      ? "join"
      : "signin";
  const [tab, setTab] = useState(initialTab);

  // Sign-in state
  const [customerId, setCustomerId] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [siLoading, setSiLoading] = useState(false);

  // Sign-up state
  const [form, setForm] = useState({
    name: sp.get("name") || "",
    phone: "",
    email: sp.get("email") || "",
    country_code: "AE",
    preferred_currency: "AED",
    preferred_channel: "WhatsApp",
    gdpr_consent: true,
    marketing_consent: false,
    referred_by: sp.get("ref") || "",
  });
  const [suLoading, setSuLoading] = useState(false);

  useEffect(() => {
    if (sp.get("ref")) setTab("join");
  }, [sp]);

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSiLoading(true);
    try {
      const { data } = await api.post("/auth/customer/login", {
        customer_id: customerId.trim().toUpperCase(),
        identifier: identifier.trim(),
      });
      login(data.access_token, { ...data.user, role: "customer" });
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
      nav("/customer");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSiLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) {
      toast.error("Please accept the privacy notice to continue.");
      return;
    }
    setSuLoading(true);
    try {
      let data;
      if (googleToken) {
        // Google two-step flow — finalize with the pending token + phone
        const resp = await api.post("/auth/google/finalize", {
          pending_token: googleToken,
          phone: form.phone,
          country_code: form.country_code,
          preferred_currency: form.preferred_currency,
          preferred_channel: form.preferred_channel,
          marketing_consent: form.marketing_consent,
          referred_by: form.referred_by.trim().toUpperCase() || undefined,
        });
        data = resp.data;
      } else {
        const resp = await api.post("/auth/customer/register", {
          ...form,
          referred_by: form.referred_by.trim().toUpperCase() || undefined,
        });
        data = resp.data;
      }
      login(data.access_token, { ...data.user, role: "customer" });
      toast.success(`Welcome to Zola Rewards — your ID is ${data.user.customer_id}`);
      nav("/customer");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSuLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Preserve any referral code across the Google roundtrip — Google's
    // callback URL has no query string of ours, so we stash `?ref=` in
    // sessionStorage and re-attach it in GoogleAuthCallback.
    const ref = sp.get("ref");
    if (ref) {
      try { sessionStorage.setItem("zola_pending_ref", ref.trim().toUpperCase()); }
      catch { /* private mode — ignore */ }
    }
    const redirectUrl = window.location.origin + "/auth/google/callback";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" +
      encodeURIComponent(redirectUrl);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div className="hidden md:block landing-hero" />
      <div className="p-6 sm:p-10 flex flex-col">
        <Link
          to="/"
          data-testid="back-to-landing"
          className="inline-flex items-center gap-2 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="my-6 max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl grid place-items-center"
                 style={{ background: "var(--navy)", color: "#fff" }}>
              {tab === "signin" ? <IdCard className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
            </div>
            <div>
              <div className="label-caps gold">Members Access</div>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight"
                  style={{ letterSpacing: "-0.02em" }}>
                {tab === "signin" ? "Welcome back" : "Join Zola Rewards"}
              </h1>
            </div>
          </div>

          {/* Tabs */}
          <div
            className="flex gap-1 p-1 rounded-2xl mb-5"
            style={{
              background: "var(--bg-soft)",
              border: "1px solid var(--border)",
            }}
          >
            <TabButton active={tab === "signin"} onClick={() => setTab("signin")} testid="tab-signin">
              Sign in
            </TabButton>
            <TabButton active={tab === "join"} onClick={() => setTab("join")} testid="tab-join">
              Join now
            </TabButton>
          </div>

          {tab === "signin" ? (
            <form onSubmit={handleSignIn} className="space-y-4" data-testid="customer-login-form">
              <div className="space-y-1.5">
                <Label htmlFor="cid">Customer ID</Label>
                <Input
                  id="cid"
                  data-testid="customer-id-input"
                  placeholder="e.g. 4567 12345"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  className="font-mono uppercase tracking-wider"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ident">Phone or Email</Label>
                <Input
                  id="ident"
                  data-testid="customer-identifier-input"
                  placeholder="+9715…  or  you@example.com"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={siLoading}
                data-testid="customer-login-submit"
                className="w-full h-11 rounded-xl text-white"
                style={{ background: "var(--navy)" }}
              >
                {siLoading ? "Signing in…" : "Access my rewards"}
              </Button>
              <div className="text-center">
                <ForgotPasswordLink role="customer" />
              </div>
            </form>
          ) : (
            <form onSubmit={handleSignUp} className="space-y-3.5" data-testid="customer-signup-form">
              {googleToken && (
                <div
                  className="rounded-xl p-3 text-xs mb-1"
                  data-testid="google-finalize-banner"
                  style={{
                    background: "rgba(201,162,75,0.12)",
                    border: "1px solid rgba(201,162,75,0.35)",
                    color: "var(--text)",
                  }}
                >
                  <div className="font-semibold" style={{ color: "var(--gold)" }}>
                    One more step
                  </div>
                  Signed in with Google as{" "}
                  <span className="font-medium">{form.email}</span>. Please
                  confirm your mobile so we can issue your Member ID.
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="su-name">Full name</Label>
                <Input
                  id="su-name"
                  data-testid="signup-name-input"
                  placeholder="Aisha Al Mansoori"
                  value={form.name}
                  onChange={(e) => updateForm({ name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="su-phone">Mobile</Label>
                  <Input
                    id="su-phone"
                    data-testid="signup-phone-input"
                    placeholder="+9715…"
                    value={form.phone}
                    onChange={(e) => updateForm({ phone: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-country">Country</Label>
                  <Select
                    value={form.country_code}
                    onValueChange={(v) => {
                      const c = COUNTRIES.find((x) => x.code === v);
                      updateForm({
                        country_code: v,
                        preferred_currency: c?.currency || form.preferred_currency,
                      });
                    }}
                  >
                    <SelectTrigger id="su-country" data-testid="signup-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="su-email">Email</Label>
                <Input
                  id="su-email"
                  data-testid="signup-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => updateForm({ email: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="su-channel">Contact via</Label>
                  <Select
                    value={form.preferred_channel}
                    onValueChange={(v) => updateForm({ preferred_channel: v })}
                  >
                    <SelectTrigger id="su-channel" data-testid="signup-channel-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="SMS">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-ref">Referral code <span style={{ color: "var(--text-muted)" }}>(optional)</span></Label>
                  <Input
                    id="su-ref"
                    data-testid="signup-referral-input"
                    placeholder="9-digit ID"
                    value={form.referred_by}
                    onChange={(e) => updateForm({ referred_by: e.target.value })}
                    className="font-mono uppercase"
                  />
                </div>
              </div>

              <label className="flex items-start gap-2.5 text-xs pt-1"
                     style={{ color: "var(--text-muted)" }}>
                <Checkbox
                  data-testid="signup-gdpr-consent"
                  checked={form.gdpr_consent}
                  onCheckedChange={(v) => updateForm({ gdpr_consent: !!v })}
                  className="mt-0.5"
                />
                <span>
                  I agree that Zola Travels stores my contact information to
                  manage my rewards ledger, in line with UAE PDPL /
                  GDPR.
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-xs"
                     style={{ color: "var(--text-muted)" }}>
                <Checkbox
                  data-testid="signup-marketing-consent"
                  checked={form.marketing_consent}
                  onCheckedChange={(v) => updateForm({ marketing_consent: !!v })}
                  className="mt-0.5"
                />
                <span>Send me offers, tier upgrades and travel tips.</span>
              </label>

              <Button
                type="submit"
                disabled={suLoading}
                data-testid="customer-signup-submit"
                className="w-full h-11 rounded-xl text-white mt-2"
                style={{ background: "var(--navy)" }}
              >
                {suLoading ? "Creating…" : "Create my membership"}
              </Button>
            </form>
          )}

          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: "var(--border)" }} />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 label-caps" style={{ background: "var(--bg)" }}>or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            data-testid="google-signin-button"
            className="w-full h-11 rounded-xl font-medium flex items-center justify-center gap-3 transition-all"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border-strong)",
              color: "var(--text)",
            }}
          >
            <GoogleLogo />
            Continue with Google
          </button>

          {tab === "signin" && (
            <div className="mt-6 rounded-xl p-3 text-xs"
                 style={{ background: "var(--bg-soft)", color: "var(--text-muted)" }}>
              <span className="font-medium" style={{ color: "var(--text)" }}>
                Demo customer:
              </span>{" "}
              <span className="font-mono">ZOLA-260101-DEMO</span> ·{" "}
              aisha@example.com
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
